import React, { useState } from "react"
import classnames from "classnames"
import { Collapse, Drawer, useMediaQuery } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    AutoAwesome,
    LiveHelpRounded as ChatIcon,
    Phone as PhoneIcon,
    Telegram as TelegramIcon,
    WhatsApp as WhatsAppIcon,
} from "@mui/icons-material"
import { ReactComponent as MaxIcon } from "images/pages/iconsV2/max.svg"

import SectionHeader from "pages/CreateContract/SectionHeader"
import { mobileMaxWidthMediaQuery } from "methods/utils"
import { openInNewTab } from "methods/openInNewTab"
import { UI_LAYER_HELP_CENTER } from "constants/uiLayers"

import "pages/Styles/SupportHelpCenter.sass"

/** Видео RuTube: заголовок с площадки, краткое описание, ссылка */
const HELP_VIDEOS: { id: string; title: string; description: string; url: string }[] = [
    {
        id: "rutube_template_setup",
        title: "Настройка своего шаблона",
        url: "https://rutube.ru/video/d379fca0fd22239f97424d8af7fc9000/",
        description:
            "Объёмный видеоурок по всем этапам работы с шаблоном: загрузка документа, подписи, даты и номер, «Мои поля», формулы и типовые сценарии. В описании ролика на RuTube есть тайм-коды — можно сразу перейти к нужному фрагменту.",
    },
    {
        id: "rutube_send_to_sign",
        title: "Как выставить на подпись договор на примере посуточной аренды",
        url: "https://rutube.ru/video/8dd32d9d3ea042004db1163a7648e8eb/",
        description:
            "Пошагово выставляем первый договор на подпись на примере шаблона краткосрочного найма. Показаны вкладка «Шаблоны», работа с полями, отправка клиенту и подписание со стороны получателя.",
    },
    {
        id: "rutube_sign_shorts",
        title: "Как подписать договор на примере посуточной аренды квартиры",
        url: "https://rutube.ru/shorts/aa759f0cebcb7c251bb5fd07b2ef3ada/",
        description:
            "Короткий ролик о том, как проходит подписание договора на примере аренды квартиры. Удобно быстро показать клиенту, как выглядит процесс с его стороны.",
    },
    {
        id: "rutube_team_member",
        title: "Как добавить сотрудника в ваш аккаунт в OkiDoki",
        url: "https://rutube.ru/video/1c3b989f4d3b69f125750a782dadbd26/",
        description:
            "Разбираем, как дать доступ к аккаунту сотрудникам и совместно работать в одном кабинете: приглашение и типовые сценарии использования.",
    },
    {
        id: "rutube_realtycalendar",
        title: "Интеграция с RealtyCalendar",
        url: "https://rutube.ru/video/29f8592a82d8f3e8392b9870c70550b4/",
        description:
            "Показываем, как устроена интеграция с RealtyCalendar и какие настройки нужны, чтобы всё корректно подключилось и работало в связке с OkiDoki.",
    },
]

type Props = {
    open: boolean
    onClose: () => void
    /** Редактирование шаблона: можно открыть ИИ-помощника на странице */
    canOpenTemplateAiAssistant: boolean
    onRequestTemplateAiAssistant: () => void
    signingAssistantActive: boolean
    onOpenSigningAssistant: () => void
    shouldOfferInitiator: boolean
    getInitiatorPhone: () => string | null
    onCallInitiator: () => void
    onWhatsAppInitiator: () => void
    onTelegramInitiator: () => void
    /** Показывать Telegram/MAX поддержки OkiDoki (скрыто на экране подписания, где доступен отдельный чат) */
    showOkiDokiMessengers: boolean
    /** Подпись кнопки открытия ИИ при редактировании шаблона (эксперимент «только ИИ‑чат» — обычно «Чат техподдержки»). */
    templateAiAssistantButtonLabel?: string
    onOpenTelegramSupport: () => void
    onOpenMaxSupport: () => void
}

function SupportHelpCenter(props: Props) {
    const mobile = useMediaQuery(mobileMaxWidthMediaQuery)
    const [expanded, setExpanded] = useState<Record<string, boolean>>({
        help_initiator: false,
    })
    const [openFaq, setOpenFaq] = useState<Record<string, boolean>>({})

    function toggleSectionExpanded(sectionId: string) {
        setExpanded((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }))
    }

    function toggleFaq(key: string) {
        setOpenFaq((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    const showFooterContact =
        props.canOpenTemplateAiAssistant || props.showOkiDokiMessengers

    const templateAiAssistantLabel =
        props.templateAiAssistantButtonLabel ?? "Чат по настройке шаблона"

    return (
        <Drawer
            anchor={mobile ? "bottom" : "right"}
            open={props.open}
            onClose={props.onClose}
            PaperProps={{
                className: "support-help-center",
                sx: {
                    zIndex: UI_LAYER_HELP_CENTER,
                    width: mobile ? "100%" : 440,
                    maxWidth: "100%",
                    borderTopLeftRadius: mobile ? 16 : 0,
                    borderTopRightRadius: mobile ? 16 : 0,
                },
            }}
            ModalProps={{
                keepMounted: true,
                sx: { zIndex: UI_LAYER_HELP_CENTER },
            }}
        >
            <div className="support-help-center__header">
                <h2 className="support-help-center__title">Справочный центр</h2>
                <button type="button" className="support-help-center__close" onClick={props.onClose} aria-label="Закрыть">
                    <CloseIcon sx={{ fontSize: 22 }} />
                </button>
            </div>

            <div className="support-help-center__body">
                <div className="support-help-center__scroll">
                    {props.signingAssistantActive && (
                        <div className="support-help-center__signing-block">
                            <button
                                type="button"
                                className="support-help-center__support-btn support-help-center__support-btn--primary"
                                onClick={() => {
                                    props.onOpenSigningAssistant()
                                    props.onClose()
                                }}
                            >
                                <ChatIcon sx={{ fontSize: 22 }} />
                                Помощь при подписании
                            </button>
                        </div>
                    )}

                    {props.shouldOfferInitiator && (
                        <div className="support-help-center__section-block">
                            <SectionHeader
                                sectionId="help_initiator"
                                title={
                                    props.getInitiatorPhone()
                                        ? `Связаться с инициатором (${props.getInitiatorPhone()})`
                                        : "Связаться с инициатором"
                                }
                                expanded={!!expanded["help_initiator"]}
                                toggleSectionExpanded={toggleSectionExpanded}
                                icon={<span className="support-help-center__header-icon-placeholder" aria-hidden />}
                                body={
                                    <div className="support-help-center__initiator-nested">
                                        <button
                                            type="button"
                                            className="support-help-center__support-btn"
                                            disabled={!props.getInitiatorPhone()}
                                            onClick={() => {
                                                props.onCallInitiator()
                                                props.onClose()
                                            }}
                                        >
                                            <PhoneIcon sx={{ fontSize: 20 }} />
                                            Позвонить
                                        </button>
                                        <button
                                            type="button"
                                            className="support-help-center__support-btn"
                                            disabled={!props.getInitiatorPhone()}
                                            onClick={() => {
                                                props.onWhatsAppInitiator()
                                                props.onClose()
                                            }}
                                        >
                                            <WhatsAppIcon sx={{ fontSize: 20 }} />
                                            WhatsApp
                                        </button>
                                        <button
                                            type="button"
                                            className="support-help-center__support-btn"
                                            disabled={!props.getInitiatorPhone()}
                                            onClick={() => {
                                                props.onTelegramInitiator()
                                                props.onClose()
                                            }}
                                        >
                                            <TelegramIcon sx={{ fontSize: 20, color: "#229ED9" }} />
                                            Telegram
                                        </button>
                                    </div>
                                }
                            />
                        </div>
                    )}

                    <div className="support-help-center__faq-block">
                        <h3 className="support-help-center__subsection-title">Видеоинструкции</h3>
                        <div className="support-help-center__faq-list">
                            {HELP_VIDEOS.map((video) => {
                                const faqKey = `video-${video.id}`
                                const isOpen = !!openFaq[faqKey]
                                const panelId = `support-help-faq-panel-${faqKey}`
                                return (
                                    <div
                                        key={faqKey}
                                        className={classnames("support-help-center__faq-accordion", {
                                            "support-help-center__faq-accordion--open": isOpen,
                                        })}
                                    >
                                        <button
                                            type="button"
                                            className="support-help-center__faq-trigger"
                                            aria-expanded={isOpen}
                                            aria-controls={panelId}
                                            onClick={() => toggleFaq(faqKey)}
                                        >
                                            <span className="support-help-center__faq-q">{video.title}</span>
                                            <ExpandMoreIcon
                                                className="support-help-center__faq-chevron"
                                                aria-hidden
                                            />
                                        </button>
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <div
                                                id={panelId}
                                                className="support-help-center__faq-panel"
                                                role="region"
                                            >
                                                <p className="support-help-center__faq-a">{video.description}</p>
                                                <button
                                                    type="button"
                                                    className="support-help-center__watch-video-btn"
                                                    onClick={() => openInNewTab(video.url)}
                                                >
                                                    Смотреть видео
                                                </button>
                                            </div>
                                        </Collapse>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>

                {showFooterContact && (
                    <div className="support-help-center__footer-contact">
                        {props.canOpenTemplateAiAssistant && (
                            <button
                                type="button"
                                className="support-help-center__support-btn support-help-center__support-btn--primary support-help-center__ai-btn"
                                onClick={() => {
                                    props.onRequestTemplateAiAssistant()
                                }}
                            >
                                <AutoAwesome sx={{ fontSize: 22 }} />
                                {templateAiAssistantLabel}
                            </button>
                        )}
                        {props.showOkiDokiMessengers && (
                            <>
                                <h3 className="support-help-center__subsection-title">Техподдержка</h3>
                                <div className="support-help-center__messengers-row">
                                    <button
                                        type="button"
                                        className="support-help-center__support-btn support-help-center__messenger-half"
                                        onClick={() => {
                                            props.onOpenTelegramSupport()
                                            props.onClose()
                                        }}
                                    >
                                        <TelegramIcon className="support-help-center__messenger-icon" sx={{ color: "#229ED9" }} />
                                        Telegram
                                    </button>
                                    <button
                                        type="button"
                                        className="support-help-center__support-btn support-help-center__messenger-half"
                                        onClick={() => {
                                            props.onOpenMaxSupport()
                                            props.onClose()
                                        }}
                                    >
                                        <MaxIcon className="support-help-center__messenger-icon" />
                                        MAX
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </Drawer>
    )
}

export default SupportHelpCenter
